import React, { useContext, useState, useEffect } from "react";
import {
  MdDragHandle,
  MdDelete,
  MdClose,
  MdSchedule,
  MdBookmarkBorder,
  MdCheck,
} from "react-icons/md";
import { db } from "../../Auth/firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import GlobalContext from "../../../context/GlobalCalendarContext";
import { useProjects } from "../../../context/ProjectsContext";
import { useAuth } from "../../../context/AuthContext";

const labelsClasses = ["indigo", "gray", "green", "blue", "red", "purple"];

const labelColorClasses = {
  indigo: "bg-indigo-200",
  gray: "bg-gray-500",
  green: "bg-green-500",
  blue: "bg-blue-500",
  red: "bg-red-200",
  purple: "bg-purple-500",
};

export default function EventModal() {
  const { setShowEventModal, daySelected, dispatchCalEvent, selectedEvent } =
    useContext(GlobalContext);
  const { activeProject } = useProjects();
  const { currentUser } = useAuth();
  const [eventDetails, setEventDetails] = useState({
    title: "",
    description: "",
    label: labelsClasses[0],
  });

  useEffect(() => {
    if (selectedEvent) {
      setEventDetails({
        title: selectedEvent.title,
        description: selectedEvent.description,
        label: selectedEvent.label,
      });
    }
  }, [selectedEvent]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEventDetails((prevDetails) => ({ ...prevDetails, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const calendarEvent = {
      ...eventDetails,
      day: daySelected.valueOf(),
      projectId: activeProject.id,
      createdBy: currentUser.uid,
    };

    if (selectedEvent) {
      await updateDoc(doc(db, "events", selectedEvent.id), calendarEvent);
      dispatchCalEvent({
        type: "update",
        payload: { ...calendarEvent, id: selectedEvent.id },
      });
    } else {
      try {
        const docRef = await addDoc(collection(db, "events"), calendarEvent);
        const newEventWithId = { ...calendarEvent, id: docRef.id };
        dispatchCalEvent({ type: "push", payload: newEventWithId });
      } catch (error) {
        console.error("Error adding event: ", error);
      }
    }
    setShowEventModal(false);
  };

  const handleDelete = async () => {
    if (selectedEvent) {
      await deleteDoc(doc(db, "events", selectedEvent.id));
      dispatchCalEvent({ type: "delete", payload: selectedEvent });
      setShowEventModal(false);
    }
  };

  const canEditOrDelete = selectedEvent
    ? selectedEvent.createdBy === currentUser.uid ||
      activeProject.creator.uid === currentUser.uid
    : true;

  return (
    <div className="flex items-center justify-center fixed left-0 bottom-0 w-full h-full bg-black bg-opacity-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-auto my-8"
      >
        <header className="bg-gray-100 px-4 py-3 flex justify-between items-center">
          <MdDragHandle className="text-gray-500" />
          <div>
            {selectedEvent && canEditOrDelete && (
              <button
                onClick={handleDelete}
                className="text-gray-500 hover:text-gray-700 p-1 rounded transition duration-150 ease-in-out"
                aria-label="Delete event"
              >
                <MdDelete />
              </button>
            )}
            <button
              onClick={() => setShowEventModal(false)}
              className="text-gray-500 hover:text-gray-700 p-1 rounded transition duration-150 ease-in-out"
              aria-label="Close modal"
            >
              <MdClose />
            </button>
          </div>
        </header>
        <div className="p-4">
          <div className="space-y-4">
            <input
              type="text"
              name="title"
              placeholder="Add title"
              value={eventDetails.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 transition duration-150 ease-in-out"
              disabled={!canEditOrDelete}
            />
            <div className="flex items-center space-x-2">
              <MdSchedule className="text-gray-500" />
              <p className="text-sm">{daySelected.format("dddd, MMMM DD")}</p>
            </div>
            <input
              type="text"
              name="description"
              placeholder="Add a description"
              value={eventDetails.description}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 transition duration-150 ease-in-out"
              disabled={!canEditOrDelete}
            />
            <div className="flex items-center space-x-2">
              <MdBookmarkBorder className="text-gray-500" />
              <div className="flex gap-x-2">
                {labelsClasses.map((lblClass, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() =>
                      setEventDetails((prev) => ({ ...prev, label: lblClass }))
                    }
                    className={`${
                      labelColorClasses[lblClass]
                    } w-8 h-8 rounded-full flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out ${
                      eventDetails.label === lblClass
                        ? "ring-2 ring-offset-2 ring-white"
                        : ""
                    }`}
                    aria-label={`Label ${lblClass}`}
                    disabled={!canEditOrDelete}
                  >
                    {eventDetails.label === lblClass && (
                      <MdCheck className="text-white" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        {canEditOrDelete && (
          <footer className="flex justify-end border-t p-4">
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-150 ease-in-out"
            >
              Save
            </button>
          </footer>
        )}
      </form>
    </div>
  );
}
